import React, { useState } from 'react';
import { Button, Typography } from '@comfama/comfama-ui-react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import type { Dayjs } from 'dayjs';
import 'dayjs/locale/es';
import Breadcrumb from '@/components/atoms/breadcrumb/breadcrumb';
import './cabeceraLibranzas.scss';

interface CabeceraLibranzasCuadroTextoProps {
    nombre?: string;
    imagenUrl?: string;
    descripcion?: React.ReactNode;
    imgTitle?: string;
    breadcrumb?: string;
    interrogationDescription?: React.ReactNode;
    realizadosImagenUrl?: string;
    realizadosImgTitle?: string;
    realizadosDescripcion?: React.ReactNode;
}

export interface BuscarHistorialParams {
    tipoDocumento: string;
    numDocumento: string;
    fechaInicio: string;
    fechaFin: string;
}

interface CabeceraLibranzasForm {
    formTitle?: string;
    razonSocial?: string;
    numDocumentoEmpresa?: number;
    onConsultaChange?: (isConsulta: boolean) => void;
    onBuscar?: (params: BuscarHistorialParams) => void;
}

type CabeceraLibranzasProps = CabeceraLibranzasCuadroTextoProps &
    CabeceraLibranzasForm;

const CabeceraLibranzas: React.FC<CabeceraLibranzasProps> = ({
    nombre,
    imagenUrl,
    descripcion,
    imgTitle,
    breadcrumb,
    formTitle,
    razonSocial,
    numDocumentoEmpresa,
    interrogationDescription,
    realizadosImagenUrl,
    realizadosImgTitle,
    realizadosDescripcion,
    onConsultaChange,
    onBuscar,
}) => {
    const [showForm, setShowForm] = useState(false);
    const [fechaInicial, setFechaInicial] = useState<Dayjs | null>(null);
    const [fechaFinal, setFechaFinal] = useState<Dayjs | null>(null);
    const [tipoDocumento, setTipoDocumento] = useState('');
    const [numDocumento, setNumDocumento] = useState('');

    const handleLinkClick = () => {
        setShowForm(true);
        onConsultaChange?.(true);
    };

    const handleVolver = () => {
        setShowForm(false);
        onConsultaChange?.(false);
    };

    const handleBuscar = () => {
        setShowForm(true);
        onBuscar?.({
            tipoDocumento,
            numDocumento,
            fechaInicio: fechaInicial?.format('YYYY-MM-DD') ?? '',
            fechaFin: fechaFinal?.format('YYYY-MM-DD') ?? '',
        });
    };

    return (
        <div className="o-cabecera-libranzas">
            <div className="o-cabecera-libranzas__container">
                <div className="o-cabecera-libranzas__cuadro-texto">
                    <div className={`o-cabecera-libranzas__contenido${showForm && interrogationDescription ? ' o-cabecera-libranzas__contenido--consulta' : ''}`}>
                        <div className="o-cabecera-libranzas__text-content">
                            {showForm && interrogationDescription ? (
                                <div className="o-cabecera-libranzas__consulta-col">
                                    <Breadcrumb content={breadcrumb} />
                                    <Typography variant="h4">{nombre}</Typography>
                                    <div className="o-cabecera-libranzas__consulta-media">
                                        <div className="o-cabecera-libranzas__imagen">
                                            <img alt={realizadosImgTitle || ''} src={realizadosImagenUrl} />
                                        </div>
                                        {realizadosDescripcion && (
                                            <div className="o-cabecera-libranzas__richtext">
                                                <Typography variant="body1">
                                                    {realizadosDescripcion}
                                                </Typography>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <Breadcrumb content={breadcrumb} />
                                    <div>
                                        <Typography variant="h4">
                                            {nombre}
                                        </Typography>
                                        {descripcion && (
                                            <div className="o-cabecera-libranzas__richtext">
                                                <Typography variant="body1">
                                                    {descripcion}
                                                </Typography>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                        {!(showForm && interrogationDescription) && (
                            <div className="o-cabecera-libranzas__imagen">
                                <img alt={imgTitle || ''} src={imagenUrl} />
                            </div>
                        )}
                    </div>
                </div>
                <div className="o-cabecera-libranzas__form">
                    {showForm ? (
                        <>
                            <div className="o-cabecera-libranzas__form-content">
                                <Typography variant="h4">
                                    Detalle de deducción
                                </Typography>
                                <div className="o-cabecera-libranzas__form-fields">
                                    <select
                                        className={`o-cabecera-libranzas__form-select${tipoDocumento ? '' : ' o-cabecera-libranzas__form-select--placeholder'}`}
                                        value={tipoDocumento}
                                        onChange={(e) => setTipoDocumento(e.target.value)}
                                    >
                                        <option value="">Elige una opción</option>
                                        <option value="CC">Cédula de ciudadanía</option>
                                        <option value="CE">Cédula de extranjería</option>
                                        <option value="TI">Tarjeta de identidad</option>
                                        <option value="PP">Pasaporte</option>
                                    </select>
                                    <input
                                        type="text"
                                        className="o-cabecera-libranzas__form-input"
                                        placeholder="Ingresa número de documento"
                                        value={numDocumento}
                                        onChange={(e) => setNumDocumento(e.target.value)}
                                    />
                                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                                        <DatePicker
                                            label="Fecha inicial"
                                            value={fechaInicial}
                                            onChange={(val) => setFechaInicial(val)}
                                            slotProps={{ openPickerButton: { sx: { color: '#EE2B7B' } } }}
                                        />
                                        <DatePicker
                                            label="Fecha final"
                                            value={fechaFinal}
                                            onChange={(val) => setFechaFinal(val)}
                                            minDate={fechaInicial ?? undefined}
                                            slotProps={{ openPickerButton: { sx: { color: '#EE2B7B' } } }}
                                        />
                                    </LocalizationProvider>
                                </div>
                            </div>
                            <div className="o-cabecera-libranzas__form-link">
                                {onBuscar && (
                                    <Button variant="outlined" action={handleVolver}>
                                        Volver
                                    </Button>
                                )}
                                <Button
                                    variant="contained"
                                    action={onBuscar ? handleBuscar : handleVolver}
                                >
                                    Buscar
                                </Button>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="o-cabecera-libranzas__form-content">
                                <Typography variant="h4" className="o-cabecera-libranzas__form-content-titulo">
                                    {formTitle}
                                </Typography>
                                <div className='o-cabecera-libranzas__form-content-text'>
                                    <span><strong>Razón social: </strong> {razonSocial}</span>
                                </div>
                                <div className='o-cabecera-libranzas__form-content-text'>
                                    <span><strong>Documento: </strong> {numDocumentoEmpresa}</span>
                                </div>
                            </div>
                            <div className="o-cabecera-libranzas__form-link">
                                <Button variant="text" action={handleLinkClick}>
                                    Consulta los pagos realizados
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CabeceraLibranzas;
